import { DOCUMENT } from '@angular/common';
import { computed, Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';

import { getLastSectionSelector } from '../../../../menu-categories/store/menu-categories.selectors';
import { ZenitEventsContextConfigs } from '../../configs/event.configs';
import * as ZenitContextHelpers from '../../helpers/context.helper';
import * as PayloadHelper from '../../helpers/payload.helper';
import {
    ZenitEventData,
    ZenitEventName,
    ZenitEventParam,
    ZenitEventValues,
} from '../../models/zenit-general.model';
import { getGridPageProductsFiltered } from '../../store/selectors/custom.selectors';

import { BasicEventParserService } from './basic-event/basic-event-parser.service';

import { CartItem } from '@api/cart/cart.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getCurrencyInfo, getLanguageCode, getLanguageLocale, getPage, getStoreId } from '@core/infrastructure/state/core.selectors';
import { selectOrderId, selectOrderItems } from '@features/orders/order-confirmation/infrastructure/state/order-confirmation.selectors';
import { ProductPublicModels } from '@features/products/product';
import { getProductsList } from '@features/products/productsArray/infrastructure/state/productsArray.selectors';
import { SastreGetRecommendedSize } from '@features/products/sastre/recommender/domain/store/sastre-recommender.selectors';
import { getSidebarLoadedComponent, getSidebarLoadedComponentData } from '@features/sidebar/infrastructure/state/sidebar.selectors';
import { WishlistPublicSelectors } from '@features/wishlist';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { getPageGridElements } from '@pages/category/modules/category-grid/store/category-grid.selectors';
import { getCategoryId, getCategoryLocationPath } from '@pages/category/store/category-page.selectors';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { getPickupPointsViewMode } from '@pages/checkout/shipping/store/pickup/pickup.selectors';
import { getProductPageDetail } from '@pages/product';
import { SearchSelectors } from '@pages/search';
import { getSelectedAddress } from '@shared/address/store/address/address.selectors';
import { EventFrom, ItemTypes } from '@shared/analytics/COMMON/models/analytics-common.models';
import { getRelatedProductsPlaces } from '@shared/product/related-products/infrastructure/state/related-products.selectors';
import { getShopCartCoreId, getShopCartCoreItems } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';

@Injectable()
export class ZenitEventParserService {
    constructor(
        private readonly store: Store,
        private readonly basicEventParserService: BasicEventParserService,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly commonWindow: CommonWindow,
    ) { }

    private getItemSelector(eventName: ZenitEventName): ProductPublicModels.PublicProduct | null {
        switch (eventName) {
            case ZenitEventName.PageHit:
                return this.store.selectSignal(getProductPageDetail)();
            default:
                return null;
        }
    }

    private generateZenitSelectors(
        config: string[],
        eventName: ZenitEventName
    ): ZenitEventValues {
        const values: ZenitEventValues = {};

        // biome-ignore lint/complexity/noForEach: <explanation>
        config.forEach((param: ZenitEventParam) => {
            switch (param) {
                case 'cartId':
                    values[param] = this.getCartIdSelector(eventName, this.store);
                    break;
                case 'items':
                    values[param] = this.store.selectSignal(selectOrderItems)();
                    break;
                case 'categoryURL':
                    values.categoryUrl = this.store.selectSignal(getCategoryLocationPath)();
                    break;
                case 'categoryId':
                    values.categoryId = this.store.selectSignal(getCategoryId)();
                    break;
                case 'catentryId':
                    values.item = this.getItemSelector(eventName);
                    break;
                case 'displayLanguage':
                    values[param] = this.store.selectSignal(getLanguageLocale)();
                    break;
                case 'list':
                    values.searchRequestQuery = this.store.selectSignal(SearchSelectors.getSearchTerm)();
                    values.searchType = this.store.selectSignal(SearchSelectors.getSearchType)();
                    values.sidebarComponent = this.store.selectSignal(getSidebarLoadedComponent)();
                    values.sidebarData = this.store.selectSignal(getSidebarLoadedComponentData)();
                    break;
                case 'listPosition':
                    values.cartItems = this.store.selectSignal(getShopCartCoreItems)();
                    values.filteredProductsIds = this.store.selectSignal(getGridPageProductsFiltered)();
                    values.productPageProduct = this.store.selectSignal(getProductPageDetail)();
                    values.productsArray = this.store.selectSignal(getProductsList)();
                    values.queryResults = this.store.selectSignal(SearchSelectors.getQueryResults)();
                    values.relatedProductsPlaces = this.store.selectSignal(getRelatedProductsPlaces)();
                    values.wishListItems = this.store.selectSignal(WishlistPublicSelectors.getWishlistItems)();
                    break;
                case 'pageType':
                    values.cartItems = this.store.selectSignal(getShopCartCoreItems)();
                    values.currentUrl = this.store.selectSignal(routerPublicQueries.getUrlFromRouter)();
                    values.isSelectedAddress = computed(() => {
                        return !!this.store.selectSignal(getSelectedAddress)();
                    })();
                    values.page = this.store.selectSignal(getPage)();
                    values.productPageProduct = this.store.selectSignal(getProductPageDetail)();
                    values.searchRequestQuery = this.store.selectSignal(SearchSelectors.getSearchTerm)();
                    values.selectedViewPickup = this.store.selectSignal(getPickupPointsViewMode)();
                    break;
                case 'personalization':
                    values.relatedProductsPlaces = this.store.selectSignal(getRelatedProductsPlaces)();
                    break;
                case 'recommender':
                    values.recommenderSize = this.store.selectSignal(SastreGetRecommendedSize())();
                    break;
                case 'section':
                case 'universe':
                    values[param] = this.store.selectSignal(getLastSectionSelector)();
                    break;
                case 'skus':
                    // Lógica en el helper
                    break;
                case 'storeId':
                    values[param] = this.store.selectSignal(getStoreId)();
                    break;
                case 'isRecommended':
                    values.recommenderSize = this.store.selectSignal(SastreGetRecommendedSize())();
                    break;
                case 'currency':
                    values[param] = this.store.selectSignal(getCurrencyInfo)();
                    break;
                case 'zoomLevel':
                    values[param] = this.store.selectSignal(getGridType)();
                    break;
                case 'searchRequestQuery':
                    values[param] = this.store.selectSignal(SearchSelectors.getSearchTerm)();
                    break;
                case 'searchRequestLocale':
                    values[param] = this.store.selectSignal(getLanguageCode)();
                    break;
                case 'searchRequestOffset':
                    values[param] = this.store.selectSignal(SearchSelectors.getSearchOffset)();
                    break;
                case 'searchRequestFilters':
                    values[param] = this.store.selectSignal(SearchSelectors.getSelectedFilters)();
                    break;
                case 'templateType':
                    values.pageGridElements = this.store.selectSignal(getPageGridElements)();
                    values.zoomLevel = this.store.selectSignal(getGridType)();
                    break;
                default:
                    break;
            }
        });

        return values;
    }

    private getValueForContextProperty(
        propertyName: ZenitEventParam,
        targetItem: ItemTypes,
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow
    ): any {
        switch (propertyName) {
            case 'action':
                return _eventData.action;
            case 'assetId':
                return ZenitContextHelpers.getItemMediaExtraInfoProperty(targetItem, 'assetId');
            case 'brandId':
                // Valor fijo para MD
                return 11;
            case 'cartId':
                return ZenitContextHelpers.getCartId(values.cartId);
            case 'categoryId':
                return ZenitContextHelpers.getCategoryId(values.categoryId);
            case 'categoryURL':
                return ZenitContextHelpers.getCategoryURL(values?.categoryUrl);
            case 'catentryId':
                return ZenitContextHelpers.getCatentryId(targetItem);
            case 'skus':
                return ZenitContextHelpers.getSkus([targetItem]);
            case 'list':
                return ZenitContextHelpers.getList(
                    eventFrom,
                    values.page,
                    targetItem,
                    {
                        place: _eventData?.place,
                        searchRequestQuery: values?.searchRequestQuery,
                        searchType: values?.searchType,
                    },
                    { name: values.sidebarComponent, data: values.sidebarData },
                );
            case 'listPosition':
                return ZenitContextHelpers.getProductItemIndex<ItemTypes>(
                    targetItem,
                    eventFrom,
                    values,
                    _eventData
                );
            case 'pageType':
                return ZenitContextHelpers.getPageType(values, eventFrom, _eventData, eventName);
            case 'partnumber':
                return ZenitContextHelpers.getReferenceStyle(targetItem);
            case 'personalization':
                return ZenitContextHelpers.getPersonalization(
                    targetItem,
                    values?.relatedProductsPlaces,
                    _eventData?.place,
                    _eventData?.relatedsSectionId
                );
            case 'quantity':
                return ZenitContextHelpers.getQuantity(_eventData?.quantity, targetItem);
            case 'recommender':
                return ZenitContextHelpers.getRecommender(eventName, _eventData?.action, values?.recommenderSize);
            case 'section':
            case 'universe':
                return ZenitContextHelpers.getSection(values.section, commonWindow);
            case 'sku':
                return ZenitContextHelpers.getPartNumber(targetItem);
            case 'currency':
                return ZenitContextHelpers.getCurrency(values.currency);
            case 'items':
                return ZenitContextHelpers.getItems(
                    (_eventData?.items || values?.items) as unknown as CartItem[]
                );
            case 'storeId':
                return ZenitContextHelpers.getStoreId(values.storeId);
            case 'viewOrigin':
                return ZenitContextHelpers.getItemMediaExtraInfoProperty(targetItem, 'originalName');
            case 'experiments':
                return ZenitContextHelpers.getExperiments(commonWindow);
            case 'isRecommended':
                return ZenitContextHelpers.getIsRecommended(
                    eventName,
                    _eventData?.action,
                    targetItem,
                    values?.recommenderSize
                );
            case 'zoomLevel':
                return ZenitContextHelpers.getZoomLevel(values.zoomLevel);
            case 'searchRequestFilters':
            case 'searchRequestHTTPResponseCode':
            case 'searchRequestLimit':
            case 'searchRequestMethodName':
            case 'searchRequestOffset':
            case 'searchRequestPass':
            case 'searchRequestProvider':
            case 'searchRequestResponseTime':
            case 'searchRequestResultsNum':
                return ZenitContextHelpers.getSearchRequestParams(propertyName, values, _eventData);
            case 'templateType':
                return ZenitContextHelpers.getTemplateType(targetItem, values.zoomLevel, values.pageGridElements);
            default:
                return values[propertyName];
        }
    }

    private generateContextObject(
        currentItem: ItemTypes,
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        config: ZenitEventParam[],
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow
    ): Record<string, any> | Array<Record<string, any>> {
        const contextObject = {};
        const targetItem = currentItem || values?.items?.[0] || values?.item;
        // biome-ignore lint/complexity/noForEach: <explanation>
        config.forEach((property) => {
            try {
                contextObject[property] = this.getValueForContextProperty(
                    property,
                    targetItem,
                    eventName,
                    eventFrom,
                    values,
                    _eventData,
                    commonWindow
                );
            } catch {
                contextObject[property] = undefined;
            }
        });
        return contextObject;
    }

    /** Lógica para montar los objetos context según configuración */
    private getContextObject(
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        config: ZenitEventParam[],
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow,
    ): Record<string, any> | Array<Record<string, any>> {
        if (_eventData?.items?.length) {
            return _eventData.items.map((item) => {
                return this.generateContextObject(item, eventName, eventFrom, config, values, _eventData, commonWindow);
            });
        }
        return this.generateContextObject(
            _eventData?.items?.[0],
            eventName,
            eventFrom,
            config,
            values,
            _eventData,
            commonWindow
        );
    }

    private getEventContext(
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        eventData: ZenitEventData,
        commonWindow: CommonWindow,
    ): Record<string, any> | Array<Record<string, any>> {
        try {
            const config = ZenitEventsContextConfigs[eventName];

            const selectorsObj = this.generateZenitSelectors(config, eventName);
            return this.getContextObject(eventName, eventFrom, config, selectorsObj, eventData, commonWindow);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    public getEventObject<EventObject>(
        eventName: ZenitEventName,
        eventFrom?: EventFrom,
        eventData?: ZenitEventData
    ): EventObject {
        const basicData = this.basicEventParserService.getBasicEvent();
        const eventContext = this.getEventContext(eventName, eventFrom, eventData, this.commonWindow);

        return {
            ...basicData,
            eventName,
            payload: PayloadHelper.getPayload(eventContext, this.document?.cookie),
        } as EventObject;
    }

    private getCartIdSelector(eventName: ZenitEventName, store: Store): number {
        if (eventName === ZenitEventName.AddToCart) {
            return store.selectSignal(getShopCartCoreId)();
        } if (eventName === ZenitEventName.PurchaseConfirmed) {
            return store.selectSignal(selectOrderId)();
        }
    }
}
